<template>
  <div class="program-areas-page">
    <section class="program-areas-hero">
      <div class="hero-copy">
        <span class="hero-eyebrow">Catalog Management</span>
        <h1>Manage program areas.</h1>
        <p class="hero-description">
          Create, update, and maintain the shared program-area catalog used across registrations,
          review workflows, and reporting.
        </p>
      </div>

      <div class="hero-stats">
        <div class="stat-card">
          <span class="stat-label">Visible Program Areas</span>
          <strong class="stat-value">{{ filteredProgramAreas.length }}</strong>
        </div>
      </div>
    </section>

    <section class="list-panel">
      <div class="panel-heading panel-heading--row">
        <div>
          <p class="panel-kicker">Directory</p>
          <h2>Current program areas</h2>
          <p class="panel-copy">
            Each item can include a short description to make selection easier across forms.
          </p>
        </div>

        <div class="panel-tools">
          <label class="search-shell" for="program-area-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="program-area-search"
              v-model.trim="search"
              type="search"
              placeholder="Search program areas"
            />
          </label>

          <div class="panel-actions">
            <button type="button" class="btn btn-success add-btn" @click="openCreateModal">
              <i class="fas fa-plus" aria-hidden="true"></i>
              <span>Add Program Area</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="feedback-state">
        <div class="spinner-border text-success" role="status"></div>
        <p>Loading program areas...</p>
      </div>

      <div v-else-if="!filteredProgramAreas.length" class="feedback-state feedback-state--empty">
        <h3>No program areas found</h3>
        <p>
          {{
            search ? 'Try another search term.' : 'Create the first program area to get started.'
          }}
        </p>
      </div>

      <div v-else>
        <div class="table-shell">
          <div class="table-scroll-shell">
            <b-table
              thead-class="custom-thead"
              tbody-tr-class="table-row"
              :items="paginatedProgramAreas"
              :fields="fields"
              borderless
              class="premium-table"
            >
              <template #cell(index)="data">
                <span class="index-pill">{{ padIndex(startIndex + data.index) }}</span>
              </template>

              <template #cell(name)="data">
                <div class="table-copy">
                  <strong>{{ data.item.name || 'Unnamed program area' }}</strong>
                </div>
              </template>

              <template #cell(description)="data">
                <span class="table-description">
                  {{ data.item.description || 'No description provided.' }}
                </span>
              </template>

              <template #cell(created_at)="data">
                <span>{{ formatDate(data.item.created_at) }}</span>
              </template>

              <template #cell(updated_at)="data">
                <span>{{ formatDate(data.item.updated_at) }}</span>
              </template>

              <template #cell(action)="data">
                <div class="table-actions">
                  <button
                    type="button"
                    class="icon-action-btn icon-action-btn--delete"
                    :disabled="deletingId === data.item.id"
                    @click="removeProgramArea(data.item)"
                    title="Delete program area"
                    aria-label="Delete program area"
                  >
                    <i
                      :class="
                        deletingId === data.item.id ? 'fas fa-spinner fa-spin' : 'fas fa-trash'
                      "
                    ></i>
                  </button>
                  <button
                    type="button"
                    class="icon-action-btn icon-action-btn--edit"
                    @click="openEditModal(data.item)"
                    title="Edit program area"
                    aria-label="Edit program area"
                  >
                    <i class="fas fa-pen"></i>
                  </button>
                </div>
              </template>
            </b-table>
          </div>
        </div>

        <div class="table-footer">
          <div class="pagination-summary">
            <span class="pagination-summary__eyebrow">Showing</span>
            <p class="pagination-range">
              {{ startIndex + 1 }} - {{ endIndex }} of {{ filteredProgramAreas.length }}
            </p>
          </div>

          <div class="pagination-controls">
            <div class="page-size-switcher" role="group" aria-label="Rows per page">
              <button
                v-for="size in [5, 10, 20]"
                :key="size"
                type="button"
                class="page-size-chip"
                :class="{ 'page-size-chip--active': perPage === size }"
                @click="perPage = size"
              >
                {{ size }}
              </button>
            </div>

            <b-pagination
              v-model="currentPage"
              :total-rows="filteredProgramAreas.length"
              :per-page="perPage"
              pills
              size="sm"
              class="premium-pagination"
            ></b-pagination>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showModal" class="program-modal-backdrop" @click.self="closeModal">
      <div class="program-modal">
        <div class="program-modal__hero">
          <div class="program-modal__hero-copy">
            <span class="program-modal__eyebrow">Program Area</span>
            <h3>{{ isEditing ? 'Refine this program area' : 'Create a new program area' }}</h3>
            <p>Shape a clean shared taxonomy for NGOs, projects, and reporting workflows.</p>
          </div>
          <button
            type="button"
            class="program-modal__close"
            @click="closeModal"
            aria-label="Close modal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="program-modal__body">
          <div class="program-modal__preview">
            <div class="preview-card">
              <span class="preview-card__label">Live Preview</span>
              <strong class="preview-card__title">
                {{ form.name || 'Program area title' }}
              </strong>
              <p class="preview-card__description">
                {{
                  form.description ||
                  'A concise description here will help teams choose the right program area confidently.'
                }}
              </p>
            </div>

            <div class="preview-note">
              <span class="preview-note__title">Why this matters</span>
              <p>
                Program areas appear in registration forms, filters, and review workflows, so clear
                naming keeps data entry consistent.
              </p>
            </div>
          </div>

          <form class="program-form" @submit.prevent="submitProgramArea">
            <div class="form-field">
              <label for="program-area-name">Program area name</label>
              <input
                id="program-area-name"
                v-model.trim="form.name"
                type="text"
                class="form-control"
                placeholder="e.g. Disease surveillance"
              />
            </div>

            <div class="form-field">
              <label for="program-area-description">Description</label>
              <textarea
                id="program-area-description"
                v-model.trim="form.description"
                class="form-control form-control--textarea"
                rows="5"
                placeholder="Write a short, clear description of this program area"
              ></textarea>
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="btn btn-outline-secondary form-action-secondary"
                @click="closeModal"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-success form-action-primary"
                :disabled="isSubmitDisabled"
              >
                {{ isEditing ? 'Save Changes' : 'Create Program Area' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createProgramArea,
  deleteProgramArea,
  getProgramAreas,
  updateProgramArea,
} from '../../config/api-modules/api.programAreas';

export default {
  name: 'AdminProgramAreas',
  data() {
    return {
      isLoading: false,
      isSaving: false,
      showModal: false,
      deletingId: null,
      search: '',
      currentPage: 1,
      perPage: 10,
      programAreas: [],
      fields: [
        { key: 'index', label: '#' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'created_at', label: 'Created' },
        { key: 'updated_at', label: 'Updated' },
        { key: 'action', label: '' },
      ],
      form: {
        id: null,
        name: '',
        description: '',
      },
    };
  },
  computed: {
    isEditing() {
      return this.form.id !== null;
    },
    isSubmitDisabled() {
      return this.isSaving || !this.form.name.trim() || !this.form.description.trim();
    },
    filteredProgramAreas() {
      if (!this.search) {
        return this.programAreas;
      }

      const query = this.search.toLowerCase();
      return this.programAreas.filter(
        (item) =>
          item?.name?.toLowerCase().includes(query) ||
          item?.description?.toLowerCase().includes(query)
      );
    },
    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.perPage, this.filteredProgramAreas.length);
    },
    paginatedProgramAreas() {
      return this.filteredProgramAreas.slice(this.startIndex, this.startIndex + this.perPage);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
  },
  async mounted() {
    await this.loadProgramAreas();
  },
  methods: {
    normalizeResults(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (Array.isArray(payload?.results)) {
        return payload.results;
      }

      if (Array.isArray(payload?.data?.results)) {
        return payload.data.results;
      }

      if (Array.isArray(payload?.data)) {
        return payload.data;
      }

      return [];
    },
    padIndex(index) {
      return `${index + 1}`.padStart(2, '0');
    },
    formatDate(value) {
      if (!value) {
        return 'N/A';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return 'N/A';
      }

      return date.toLocaleDateString();
    },
    async loadProgramAreas() {
      this.isLoading = true;

      try {
        const data = await getProgramAreas();
        this.programAreas = this.normalizeResults(data);
      } catch (error) {
        this.programAreas = [];
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to load program areas',
        });
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = {
        id: null,
        name: '',
        description: '',
      };
    },
    openCreateModal() {
      this.resetForm();
      this.showModal = true;
    },
    openEditModal(item) {
      this.form = {
        id: item.id,
        name: item.name || '',
        description: item.description || '',
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },
    async submitProgramArea() {
      if (this.isSubmitDisabled) {
        return;
      }

      this.isSaving = true;

      try {
        const payload = {
          name: this.form.name.trim(),
          description: this.form.description.trim(),
        };

        if (this.isEditing) {
          await updateProgramArea(this.form.id, payload);
          this.$toast.show({
            type: 'SUCCESS',
            message: 'Program area updated successfully',
          });
        } else {
          await createProgramArea(payload);
          this.$toast.show({
            type: 'SUCCESS',
            message: 'Program area created successfully',
          });
        }

        this.closeModal();
        await this.loadProgramAreas();
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: this.isEditing
            ? 'Unable to update program area'
            : 'Unable to create program area',
        });
      } finally {
        this.isSaving = false;
      }
    },
    async removeProgramArea(item) {
      const confirmation = await this.$swal({
        title: 'Delete program area?',
        text: `"${item.name}" will be removed from the catalog.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      this.deletingId = item.id;

      try {
        await deleteProgramArea(item.id);
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Program area deleted successfully',
        });

        if (
          this.startIndex >= Math.max(this.filteredProgramAreas.length - 1, 0) &&
          this.currentPage > 1
        ) {
          this.currentPage -= 1;
        }

        await this.loadProgramAreas();
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to delete program area',
        });
      } finally {
        this.deletingId = null;
      }
    },
  },
};
</script>

<style scoped>
.program-areas-page {
  display: grid;
  gap: 1.5rem;
  padding-top: 1.5rem;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.program-areas-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.75rem;
  padding: 2rem;
  border-radius: 32px;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.18), transparent 34%),
    radial-gradient(circle at bottom left, rgba(27, 86, 45, 0.12), transparent 28%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(236, 246, 239, 0.98));
  border: 1px solid rgba(29, 70, 44, 0.08);
  box-shadow: 0 24px 42px rgba(17, 47, 28, 0.08);
  min-width: 0;
}

.hero-copy {
  flex: 1 1 auto;
  max-width: none;
  min-width: 0;
}

.hero-eyebrow,
.panel-kicker,
.stat-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #2a8a43;
}

.hero-eyebrow {
  padding: 0.38rem 0.8rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
}

.hero-copy h1,
.panel-heading h2,
.program-modal h3 {
  margin: 1rem 0 0.85rem;
  color: #153322;
}

.hero-copy h1 {
  max-width: none;
  font-size: clamp(2.1rem, 4vw, 3.3rem);
  line-height: 1.02;
  letter-spacing: -0.03em;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: normal;
  hyphens: auto;
}

.hero-description,
.panel-copy {
  margin: 0;
  color: #587062;
  line-height: 1.65;
}

.hero-description {
  max-width: none;
  font-size: 1rem;
}

.hero-stats {
  display: grid;
  grid-template-columns: minmax(180px, 240px);
  gap: 1rem;
  min-width: 0;
  align-content: start;
  flex: 0 0 240px;
}

.stat-card,
.list-panel,
.program-modal {
  padding: 1.25rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(29, 70, 44, 0.08);
  box-shadow: 0 14px 30px rgba(21, 55, 33, 0.05);
  min-width: 0;
  max-width: 100%;
}

.stat-value {
  display: block;
  margin-top: 0.4rem;
  font-size: 1.6rem;
  color: #153322;
}

.stat-card {
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.stat-label {
  justify-content: flex-start;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.45;
}

.panel-heading--row,
.panel-tools {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.panel-tools {
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.panel-actions {
  display: flex;
  align-items: center;
  min-width: 0;
}

.search-shell {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 260px;
  padding: 0 1rem;
  border-radius: 16px;
  border: 1px solid rgba(29, 70, 44, 0.12);
  background: #fff;
}

.search-shell i {
  color: #5e7867;
}

.search-shell input {
  width: 100%;
  min-height: 3.2rem;
  border: 0;
  padding: 0;
  outline: none;
  background: transparent;
}

.add-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 3rem;
  padding: 0 1.05rem;
  border-radius: 14px;
  color: #ffffff !important;
  border: 0;
  background: linear-gradient(135deg, #2f8d49, #215f33);
  box-shadow: 0 14px 24px rgba(42, 138, 67, 0.18);
  max-width: 100%;
}

.add-btn span {
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}

.add-btn:hover,
.add-btn:focus {
  color: #ffffff !important;
  background: linear-gradient(135deg, #349451, #1b562d);
}

.add-btn i {
  font-size: 0.82rem;
}

.table-shell {
  margin-top: 1.5rem;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: #fff;
}

.program-areas-page ::v-deep .premium-table {
  margin-bottom: 0;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(34, 108, 52, 0.4) rgba(237, 244, 238, 0.95);
}

.table-scroll-shell::-webkit-scrollbar {
  height: 9px;
}

.table-scroll-shell::-webkit-scrollbar-track {
  background: rgba(237, 244, 238, 0.95);
  border-radius: 999px;
}

.table-scroll-shell::-webkit-scrollbar-thumb {
  background: rgba(34, 108, 52, 0.4);
  border-radius: 999px;
}

.program-areas-page ::v-deep .premium-table thead th {
  border-top: 0;
  border-bottom: 1px solid rgba(29, 70, 44, 0.08);
  background: #f4f8f5;
  color: #456454;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.program-areas-page ::v-deep .premium-table tbody td {
  vertical-align: top;
  padding: 1rem;
  border-top: 1px solid rgba(29, 70, 44, 0.06);
  color: #234031;
  white-space: nowrap;
}

.program-areas-page ::v-deep .premium-table table {
  min-width: 760px;
  table-layout: auto;
}

.index-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.4rem;
  min-height: 2.1rem;
  padding: 0 0.65rem;
  border-radius: 999px;
  background: #e8f5eb;
  color: #226c34;
  font-size: 0.78rem;
  font-weight: 700;
}

.table-copy strong {
  color: #173425;
  font-size: 0.98rem;
  display: inline-block;
  min-width: 180px;
  white-space: normal;
  word-break: break-word;
}

.table-description {
  display: inline-block;
  min-width: 260px;
  max-width: 28rem;
  color: #536c5d;
  line-height: 1.6;
  white-space: normal;
}

.table-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  align-items: center;
}

.icon-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 999px;
  border: 1px solid transparent;
  background: #f2f7f3;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.icon-action-btn i {
  font-size: 0.9rem;
}

.icon-action-btn--edit {
  color: #226c34;
  border-color: rgba(34, 108, 52, 0.18);
}

.icon-action-btn--edit:hover {
  background: #e8f5eb;
}

.icon-action-btn--delete {
  color: #c0392b;
  border-color: rgba(192, 57, 43, 0.16);
  background: #fbefed;
}

.icon-action-btn--delete:hover {
  background: #f8dfdb;
}

.icon-action-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.feedback-state {
  display: grid;
  place-items: center;
  gap: 0.75rem;
  min-height: 220px;
  color: #547061;
  text-align: center;
  margin-top: 1.5rem;
}

.feedback-state--empty {
  border: 1px dashed rgba(29, 70, 44, 0.15);
  border-radius: 20px;
  background: rgba(248, 251, 249, 0.85);
}

.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding: 1rem 1.1rem;
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(247, 250, 248, 0.98), rgba(240, 246, 242, 0.92));
  border: 1px solid rgba(29, 70, 44, 0.08);
}

.pagination-summary {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.pagination-summary__eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.08);
  color: #2a8a43;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.pagination-range {
  margin: 0;
  color: #5a7465;
  font-weight: 600;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
  min-width: 0;
}

.page-size-switcher {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(29, 70, 44, 0.08);
}

.page-size-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.35rem;
  line-height: 1;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #587062;
  font-weight: 700;
  padding: 0;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.page-size-chip:hover {
  background: rgba(42, 138, 67, 0.08);
  color: #226c34;
}

.page-size-chip--active {
  background: linear-gradient(135deg, #2f8d49, #215f33);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(42, 138, 67, 0.16);
}

.program-areas-page ::v-deep .premium-pagination {
  margin-bottom: 0;
}

.program-areas-page ::v-deep .premium-pagination .page-item .page-link {
  border: 0;
  border-radius: 999px !important;
  min-width: 2.35rem;
  height: 2.35rem;
  margin: 0 0.18rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4f6d5a;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: none;
}

.program-areas-page ::v-deep .premium-pagination .page-item.active .page-link {
  background: linear-gradient(135deg, #2f8d49, #215f33);
  color: #ffffff;
  box-shadow: 0 10px 20px rgba(42, 138, 67, 0.16);
}

.program-areas-page ::v-deep .premium-pagination .page-item.disabled .page-link {
  color: #a3b3a8;
  background: rgba(255, 255, 255, 0.56);
}

.program-modal-backdrop {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1.5rem;
  background: radial-gradient(circle at top, rgba(46, 143, 79, 0.18), transparent 22%),
    rgba(12, 25, 17, 0.52);
  backdrop-filter: blur(10px);
  z-index: 2000;
}

.program-modal {
  width: min(100%, 960px);
  padding: 0;
  overflow: hidden;
  border-radius: 30px;
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.13), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(245, 250, 246, 0.98));
  box-shadow: 0 28px 80px rgba(7, 22, 13, 0.28);
}

.program-modal__hero {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 1.6rem 1.75rem 1.2rem;
  border-bottom: 1px solid rgba(29, 70, 44, 0.08);
}

.program-modal__hero-copy {
  max-width: 32rem;
}

.program-modal__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 0.38rem 0.75rem;
  border-radius: 999px;
  background: rgba(42, 138, 67, 0.1);
  color: #2a8a43;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.program-modal__hero-copy h3 {
  margin: 0.9rem 0 0.55rem;
  font-size: clamp(1.8rem, 2.8vw, 2.5rem);
  line-height: 1.04;
  color: #163424;
}

.program-modal__hero-copy p {
  margin: 0;
  color: #5d7467;
  line-height: 1.7;
}

.program-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.7rem;
  height: 2.7rem;
  border: 1px solid rgba(29, 70, 44, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #5f7467;
  font-size: 0.95rem;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
}

.program-modal__close:hover {
  transform: translateY(-1px);
  background: #ffffff;
  color: #183726;
}

.program-modal__body {
  display: grid;
  grid-template-columns: minmax(260px, 0.9fr) minmax(0, 1.2fr);
  gap: 1.25rem;
  padding: 1.4rem 1.75rem 1.75rem;
}

.program-modal__preview {
  display: grid;
  gap: 1rem;
}

.preview-card,
.preview-note {
  border-radius: 24px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: radial-gradient(circle at top right, rgba(42, 138, 67, 0.08), transparent 32%),
    rgba(255, 255, 255, 0.92);
  padding: 1.15rem 1.2rem;
}

.preview-card__label,
.preview-note__title {
  display: block;
  margin-bottom: 0.65rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #4f6d5a;
}

.preview-card__title {
  display: block;
  margin-bottom: 0.65rem;
  color: #173425;
  font-size: 1.15rem;
  line-height: 1.3;
}

.preview-card__description,
.preview-note p {
  margin: 0;
  color: #5d7467;
  line-height: 1.7;
}

.program-form {
  display: grid;
  gap: 1rem;
  padding: 1.1rem;
  border-radius: 24px;
  border: 1px solid rgba(29, 70, 44, 0.08);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.65);
}

.form-field {
  display: grid;
  gap: 0.55rem;
}

.program-form label {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: #223429;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.program-form .form-control {
  min-height: 3.45rem;
  border-radius: 18px;
  border: 1px solid rgba(29, 70, 44, 0.12);
  box-shadow: none;
  padding: 0.95rem 1rem;
  color: #173425;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(245, 249, 246, 0.95));
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.program-form .form-control:focus {
  border-color: rgba(42, 138, 67, 0.35);
  box-shadow: 0 0 0 4px rgba(42, 138, 67, 0.1);
  transform: translateY(-1px);
}

.program-form .form-control--textarea {
  min-height: 11rem;
  padding-top: 1rem;
  resize: vertical;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 0.35rem;
}

.form-action-primary,
.form-action-secondary {
  min-height: 3.3rem;
  border-radius: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.form-action-primary {
  flex: 1 1 0;
  border: 0;
  background: linear-gradient(135deg, #2f8d49, #215f33);
  box-shadow: 0 16px 28px rgba(42, 138, 67, 0.2);
  color: #ffffff !important;
}

.form-action-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #318f4b, #1b542d);
  color: #ffffff !important;
}

.form-action-secondary {
  min-width: 140px;
  border-color: rgba(29, 70, 44, 0.2);
  color: #355645;
  background: rgba(255, 255, 255, 0.88);
}

@media (max-width: 991px) {
  .program-areas-page {
    padding-top: 0;
  }

  .program-areas-hero,
  .panel-heading--row {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-stats,
  .search-shell,
  .program-modal {
    min-width: 0;
    width: 100%;
  }

  .panel-tools {
    width: 100%;
    justify-content: stretch;
  }

  .panel-actions {
    width: 100%;
  }

  .add-btn {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  .program-modal__body {
    grid-template-columns: 1fr;
  }

  .table-footer {
    align-items: flex-start;
  }

  .pagination-controls {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
  }
}

@media (max-width: 767px) {
  .program-areas-page {
    padding-top: 0;
  }

  .program-areas-hero,
  .list-panel,
  .program-modal {
    padding: 1.15rem;
    border-radius: 22px;
  }

  .hero-copy h1 {
    font-size: clamp(1.75rem, 8.2vw, 2.35rem);
    line-height: 1.08;
  }

  .hero-description,
  .panel-copy {
    font-size: 0.95rem;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }

  .program-areas-page ::v-deep .premium-table table {
    min-width: 720px;
  }

  .program-areas-page ::v-deep .premium-table thead th,
  .program-areas-page ::v-deep .premium-table tbody td {
    padding: 0.85rem 0.75rem;
  }

  .pagination-controls {
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    align-items: center;
    padding-bottom: 0.2rem;
    -webkit-overflow-scrolling: touch;
  }

  .page-size-switcher {
    flex-shrink: 0;
  }

  .program-areas-page ::v-deep .premium-pagination {
    flex-shrink: 0;
  }

  .program-areas-page ::v-deep .premium-pagination .pagination {
    flex-wrap: nowrap;
  }
}

@media (max-width: 600px) {
  .program-areas-page {
    padding-top: 0;
  }

  .program-areas-page ::v-deep .premium-table table {
    min-width: 700px;
  }

  .table-shell {
    border-radius: 18px;
  }

  .hero-stats {
    grid-template-columns: minmax(0, 1fr);
  }

  .table-copy strong {
    min-width: 160px;
  }

  .table-description {
    min-width: 240px;
  }

  .program-modal-backdrop {
    padding: 1rem;
  }
}
</style>
